class ChangeVariantDrawerContent extends HTMLElement {
    constructor() {
        super();
        this.initializeComponent();
    }

    initializeComponent() {
        this.variantOptions = this.querySelectorAll('.product-form__controls-group input[type="radio"]');
        this.handleVariantOptionChange = this.handleVariantOptionChange.bind(this);
        this.setupEventListeners();
    }

    setupEventListeners() {
        this.variantOptions.forEach(radio => {
            radio.addEventListener('change', this.handleVariantOptionChange);
        });
        this.handleVariantOptionChange();
    }

    disconnectedCallback() {
        this.variantOptions.forEach(radio => {
            radio.removeEventListener('change', this.handleVariantOptionChange);
        });
    }

    handleVariantOptionChange() {
        const selectedVariant = this.getSelectedVariant();
        if (!selectedVariant) return;

        this.resetUIState();
        this.updateUIForVariant(selectedVariant);

        // Dispatch custom event when variant changes
        document.dispatchEvent(new CustomEvent('variant:changed', {
            detail: { variant: selectedVariant }
        }));
    }

    getSelectedVariant() {
        // data-variant-connection is the full variant.title ("A / B" for
        // multi-option products) — join every checked radio, not just one,
        // or a 2+ option product never matches and the tree stays hidden.
        const checkedRadios = Array.from(this.variantOptions).filter(radio => radio.checked);
        return checkedRadios.length > 0 ? checkedRadios.map(r => r.value).join(' / ') : null;
    }

    resetUIState() {
        this.hideAllElements();
        this.closeAllDrawers();
    }

    hideAllElements() {
        // Column-level visibility is owned by VariantPickerStepsMetafields via
        // .option-column--upcoming / --complete classes. This reset only touches
        // tile-level + drawer-trigger state so the per-variant filter below
        // (processNestedOptions → processRegularOption) can re-reveal matching
        // tiles for the new variant connection.
        const tiles = this.querySelectorAll('.option-item, .pdp-step-tile:not(.pdp-step-tile--native)');
        tiles.forEach(option => {
            option.classList.remove('active');
            option.style.display = 'none';
        });
        this.querySelectorAll('.drawer-trigger').forEach(trigger => {
            trigger.style.display = 'none';
        });
    }

    closeAllDrawers() {
        const drawers = document.querySelectorAll('.variant-options-drawer');
        drawers.forEach(drawer => drawer.classList.remove('is-active'));
        document.body.classList.remove('drawer-open');
    }

    updateUIForVariant(variantValue) {
        const nestedOptions = this.getNestedOptionsForVariant(variantValue);
        const activeParentIds = this.processNestedOptions(nestedOptions);
        this.updateDrawerTriggers(activeParentIds);
    }

    getNestedOptionsForVariant(variantValue) {
        return this.querySelectorAll(`.option-item[data-variant-connection="${variantValue}"], .pdp-step-tile[data-variant-connection="${variantValue}"]`);
    }

    processNestedOptions(nestedOptions) {
        const activeParentIds = new Set();

        nestedOptions.forEach(option => {
            const parentId = option.getAttribute('data-parent-id');

            if (this.isDrawerChildOption(option)) {
                this.processDrawerChildOption(option, parentId, activeParentIds);
            } else {
                this.processRegularOption(option, parentId);
            }
        });

        return activeParentIds;
    }

    isDrawerChildOption(option) {
        return option.classList.contains('drawer-option-child');
    }

    processDrawerChildOption(option, parentId, activeParentIds) {
        if (!parentId) return;

        const parentElement = this.querySelector(`.option-item[data-option-id="${parentId}"], .pdp-step-tile[data-option-id="${parentId}"]`);
        if (parentElement?.classList.contains('active')) {
            activeParentIds.add(parentId);
        }
    }

    processRegularOption(option, parentId) {
        // Only tile-level visibility is set here; column-level state is owned by
        // VariantPickerStepsMetafields.applyCollapseState via --upcoming/--complete.
        if (!parentId) {
            option.style.display = 'flex';
            return;
        }
        const parentElement = this.querySelector(`.option-item[data-option-id="${parentId}"], .pdp-step-tile[data-option-id="${parentId}"]`);
        if (parentElement?.classList.contains('active')) {
            option.style.display = 'flex';
        }
    }

    updateDrawerTriggers(activeParentIds) {
        activeParentIds.forEach(parentId => {
            const drawerTrigger = this.querySelector(`.drawer-trigger[data-parent-option="${parentId}"]`);
            if (drawerTrigger) {
                drawerTrigger.style.display = 'flex';
            }
        });
    }
  }

  if (!customElements.get('change-variant-drawer-content')) {
      customElements.define('change-variant-drawer-content', ChangeVariantDrawerContent);
  }


  class VariantPickerStepsMetafields extends HTMLElement {
      constructor() {
          super();
          this.initializeState();
      }

      initializeState() {
          this.container = null;
          this.restructured = null;
          this.columns = {};
          this.optionCounter = 0;
      }

      connectedCallback() {
          this.initializeComponent();
          this.setupBundleProductListeners();
          this._handleCartItemAdded = () => this.resetSelections();
          document.addEventListener('cart:item-added', this._handleCartItemAdded);
      }

      disconnectedCallback() {
          if (this._handleVariantSelectsChanged) {
              document.removeEventListener('variant-selects-changed', this._handleVariantSelectsChanged);
          }
          if (this._handleVariantChanged) {
              document.removeEventListener('variant:changed', this._handleVariantChanged);
          }
          if (this._handleDrawerClick) {
              document.removeEventListener('variant:drawer:click', this._handleDrawerClick);
          }
          if (this._handleCartItemAdded) {
              document.removeEventListener('cart:item-added', this._handleCartItemAdded);
          }
      }

      initializeComponent() {
          if (!this.findContainer()) return;
          this.processAllOptions();
          this.buildRestructuredDOM();
          this.foldDeepestColumn();
          this.setupEventListeners();

          this.updateInitialColumnTitles();
          this.mountStepHelpPills();
          this.computeOOS();
          this.applyCollapseState();

          this.setAttribute('data-hydrated', 'true');
      }

      findContainer() {
          this.container = this.querySelector('.variant-picker-steps');
          if (!this.container) {
              console.warn('No .variant-picker-steps container found');
              return false;
          }
          return true;
      }

      setupBundleProductListeners() {
          // Only listen to bundle products within this variant picker instance
          const bundleProducts = this.querySelectorAll('[data-bundle-product]');
          bundleProducts.forEach(product => {
              product.addEventListener('click', () => {
                  this.setAttribute('data-bundle-products-id', product.dataset.bundleProduct);
              });
          });
      }

      resetSelections() {
          if (!this.restructured) return;

          const tiles = this.restructured.querySelectorAll('.option-item, .pdp-step-tile');
          tiles.forEach(t => t.classList.remove('active'));

          const columns = this.restructured.querySelectorAll('.option-column');
          columns.forEach(col => {
              col.dataset.userAdvanced = 'false';
              col.classList.remove('option-column--complete', 'pdp-step-column--complete');
              const summary = col.querySelector('[data-role="summary"]');
              if (summary) {
                  summary.hidden = true;
                  const summaryText = summary.querySelector('.option-column__summary-text');
                  if (summaryText) summaryText.textContent = '';
                  const summaryThumb = summary.querySelector('.option-column__summary-thumb');
                  if (summaryThumb) summaryThumb.innerHTML = '';
              }
          });

          this.querySelectorAll('.variant-options-drawer.is-active').forEach(d => d.classList.remove('is-active'));
          document.body.classList.remove('drawer-open');
          const overlay = document.querySelector('.drawer-overlay');
          if (overlay) overlay.classList.remove('is-active');

          const sectionId = this.closest('[data-section]')?.dataset?.section;
          if (sectionId) {
              const banner = document.getElementById(`InstallationBanner-${sectionId}`);
              if (banner) {
                  banner.setAttribute('hidden', '');
                  banner.dataset.installationVideoUrl = '';
              }
          }

          this.applyCollapseState();

          const sheet = document.querySelector('picker-mobile-sheet');
          if (sheet && typeof sheet.resetSelections === 'function') {
              sheet.resetSelections();
          }

          // After ATC, picker resets — also un-resolve the ATC button so it
          // disables until the user picks a new leaf.
          document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false } }));
      }

      processAllOptions() {
          const nativeColumn = this.container.querySelector('.pdp-step-column[data-native-step="true"]');
          if (nativeColumn) {
              const tiles = nativeColumn.querySelectorAll('.pdp-step-tile--native');
              tiles.forEach((tile, idx) => {
                  tile.dataset.optionId = `native-${idx}`;
                  tile.dataset.level = '0';
                  if (!this.columns[0]) this.columns[0] = [];
                  this.columns[0].push(tile.cloneNode(true));
              });
          }

          const baseDepth = nativeColumn ? 1 : 0;
          const variantGroups = this.container.querySelectorAll('.variant-options');
          variantGroups.forEach(variantGroup => {
              const topOptions = variantGroup.querySelectorAll(':scope > .variant-option > .pdp-step-tile-wrapper, :scope > .variant-option > .option-level');
              topOptions.forEach(topOptionEl => {
                  this.processOptionLevel(topOptionEl, baseDepth, null);
              });
          });
      }

      processOptionLevel(optionEl, depth = 0, parentId = null) {
          const item = optionEl.querySelector('.option-item, .pdp-step-tile');
          if (!item) return;

          this.processOptionItem(item, depth, parentId);
          this.processNestedOptions(optionEl, depth, item);
      }

      processOptionItem(item, depth, parentId) {
          const uniqueId = this.generateUniqueId();
          this.setItemAttributes(item, uniqueId, depth, parentId);
          this.addItemToColumn(item, depth);
      }

      generateUniqueId() {
          return `option-${this.optionCounter++}`;
      }

      setItemAttributes(item, uniqueId, depth, parentId) {
          item.setAttribute('data-option-id', uniqueId);
          item.setAttribute('data-level', depth);

          if (parentId) {
              item.setAttribute('data-parent-id', parentId);
              this.handleDrawerClassification(item, parentId);
          }
      }

      addItemToColumn(item, depth) {
          if (!this.columns[depth]) {
              this.columns[depth] = [];
          }
          this.columns[depth].push(item.cloneNode(true));
      }

      handleDrawerClassification(item, parentId) {
          const parentItem = this.container.querySelector(`[data-option-id="${parentId}"]`);
          if (parentItem?.getAttribute('data-child-options-drawer') === 'true') {
              item.classList.add('drawer-option-child');
          }
      }

      processNestedOptions(optionEl, depth, item) {
          const nested = optionEl.querySelector('.pdp-step-tile-children, .nested-options');
          if (!nested) return;

          const nestedOptions = nested.querySelectorAll(':scope > .pdp-step-tile-wrapper, :scope > .option-level');
          nestedOptions.forEach(nestedEl => {
              this.processOptionLevel(
                  nestedEl,
                  depth + 1,
                  item?.getAttribute('data-option-id')
              );
          });
      }

      buildRestructuredDOM() {
          this.createRestructuredContainer();
          this.createColumns();
          this.replaceOldContainer();
      }

      createRestructuredContainer() {
          this.restructured = document.createElement('div');
          this.restructured.classList.add('restructured-variant-picker');
      }

      createColumns() {
          Object.keys(this.columns)
              .map(Number)
              .sort((a, b) => a - b)
              .forEach(this.createColumn.bind(this));
      }

      createColumn(depth) {
          const column = this.createColumnElement(depth);

          // Write title text into the header's .option-column-title slot
          const titleText = this.computeColumnTitle(depth);
          const titleSlot = column.querySelector('.option-column-title');
          if (titleSlot && titleText) {
              titleSlot.textContent = titleText;
          }

          const bodyInner = column.querySelector('.option-column__body-inner');

          const optionsWrapper = document.createElement('div');
          optionsWrapper.classList.add('options-wrapper');

          this.populateColumn(optionsWrapper, depth);

          bodyInner.appendChild(optionsWrapper);

          this.setColumnVisibility(column, depth);
          this.restructured.appendChild(column);
      }

      computeColumnTitle(depth) {
          if (depth === 0) {
              const nativeTitle = this.container.querySelector('.pdp-step-column[data-native-step="true"] .pdp-step-column__title')?.textContent?.trim();
              if (nativeTitle) return nativeTitle;
          }
          // Otherwise derive from the first regular option's data-option-label
          const firstRegular = this.columns[depth]?.find(item => item.getAttribute('data-child-options-drawer') !== 'true');
          return firstRegular?.getAttribute('data-option-label') || '';
      }

      createColumnElement(depth) {
          const column = document.createElement('div');
          column.classList.add('option-column');
          column.setAttribute('data-level', depth);

          const header = document.createElement('div');
          header.className = 'option-column__header';

          const badge = document.createElement('div');
          badge.className = 'option-column__badge';
          badge.dataset.role = 'badge';
          badge.textContent = String(depth + 1);
          header.appendChild(badge);

          const title = document.createElement('div');
          title.className = 'option-column-title';
          title.dataset.role = 'title';
          header.appendChild(title);

          const summary = document.createElement('div');
          summary.className = 'option-column__summary';
          summary.dataset.role = 'summary';
          summary.hidden = true;
          summary.innerHTML = `
              <span class="option-column__summary-thumb"></span>
              <span class="option-column__summary-text"></span>
          `;
          header.appendChild(summary);

          column.appendChild(header);

          // Body uses grid-template-rows 1fr → 0fr for smooth content-height
          // collapse. Real content lives inside .option-column__body-inner so
          // the grid track has a child to size to.
          const body = document.createElement('div');
          body.className = 'option-column__body';
          const inner = document.createElement('div');
          inner.className = 'option-column__body-inner';
          body.appendChild(inner);
          column.appendChild(body);
          column.classList.add('option-column--has-body');

          return column;
      }

      populateColumn(column, depth) {
          this.columns[depth].forEach(item => {
              column.appendChild(item);
          });
      }

      setColumnVisibility(column, depth) {
          // No-op: column visibility owned by applyCollapseState via
          // .option-column--upcoming / --complete classes.
      }

      replaceOldContainer() {
          this.container.innerHTML = '';
          this.container.appendChild(this.restructured);
      }

      foldDeepestColumn() {
          const cols = [...this.restructured.querySelectorAll(':scope > .option-column')];
          if (cols.length < 4) return;

          const last = cols[cols.length - 1];
          const host = cols[cols.length - 2];
          const hostBody = host.querySelector('.option-column__body-inner') || host.querySelector('.option-column__body');
          if (!hostBody) return;

          const heading = last.querySelector('.option-column-title')?.textContent?.trim() || 'Choose Your Year';
          // Drawer children must keep their own column — the drawer trigger and
          // its options are wired through getNextColumn(); folding them into a
          // sub-section orphans the drawer (show_options_in_drawer would be
          // silently ignored for the deepest level). Fold only non-drawer tiles
          // and keep the column alive when drawer children remain in it.
          const allTiles = [...last.querySelectorAll('.options-wrapper > .pdp-step-tile, .options-wrapper > .option-item')];
          const tiles = allTiles.filter(t => !t.classList.contains('drawer-option-child'));
          const keepColumn = tiles.length !== allTiles.length;
          if (!tiles.length) return;

          const escapeHTML = (str) => str.replace(/[<>&]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]));
          const sub = document.createElement('div');
          sub.className = 'pdp-step-sub-section';
          sub.dataset.subSectionParent = host.dataset.level;
          sub.setAttribute('hidden', '');
          sub.innerHTML = `
              <div class="pdp-step-sub-section__divider"></div>
              <div class="pdp-step-sub-section__heading">${escapeHTML(heading)}</div>
              <div class="pdp-step-sub-section__tiles options-wrapper"></div>
          `;
          const subTilesWrap = sub.querySelector('.pdp-step-sub-section__tiles');
          tiles.forEach(t => subTilesWrap.appendChild(t));

          hostBody.appendChild(sub);
          if (!keepColumn) last.remove();
      }

      setupEventListeners() {
          this.restructured.addEventListener('click', (event) => {
              // Native Step 1 tile
              const nativeTile = event.target.closest('.pdp-step-tile--native');
              if (nativeTile) {
                  event.stopPropagation();
                  this.activateNativeTile(nativeTile);
                  return;
              }

              const completedColumn = event.target.closest('.option-column--complete, .pdp-step-column--complete');
              if (completedColumn) {
                  const isDrawerColumn = !!completedColumn.querySelector('.drawer-trigger');
                  const clickedTile = event.target.closest('.option-item, .pdp-step-tile, .drawer-trigger');
                  if (!isDrawerColumn && !clickedTile) {
                      event.stopPropagation();
                      completedColumn.classList.remove('option-column--complete', 'pdp-step-column--complete');
                      delete completedColumn.dataset.userAdvanced;
                      const summary = completedColumn.querySelector('.option-column__summary');
                      if (summary) summary.hidden = true;
                      const level = parseInt(completedColumn.dataset.level, 10);
                      this.hideDeeperColumns(level);
                      this.applyCollapseState();
                      return;
                  }
              }

              const clickedItem = event.target.closest('.option-item, .pdp-step-tile');
              if (!clickedItem) return;

              event.stopPropagation();
              this.handleOptionClick(clickedItem);
          });

          const sectionId = this.closest('[data-section]')?.dataset?.section;
          if (!sectionId) return;

          const variantVideosEl = document.getElementById(`VariantInstallationVideos-${sectionId}`);
          const variantVideos = variantVideosEl ? JSON.parse(variantVideosEl.textContent) : null;

          if (!variantVideos && !this.querySelector('[data-installation-video-url]')) {
              document.getElementById(`InstallationBanner-${sectionId}`)?.remove();
          }


          const getSelectedVariantId = () => {
              const formInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
              if (formInput?.value) return formInput.value;

              const sectionEl = document.getElementById(`ProductInfo-${sectionId}`)
                  || document.querySelector(`[data-section="${sectionId}"]`);
              if (sectionEl) {
                  const input = sectionEl.querySelector('product-form input[name="id"]');
                  if (input?.value) return input.value;
              }
              return null;
          };

          const updateBannerForVariant = (variantIdOverride) => {
              const banner = document.getElementById(`InstallationBanner-${sectionId}`);
              if (!banner || !variantVideos) return;
              const variantId = variantIdOverride || getSelectedVariantId();
              const videoUrl = variantId ? variantVideos[String(variantId)] : null;
              if (videoUrl) {
                  banner.dataset.installationVideoUrl = videoUrl;
                  const thumbnail = banner.querySelector('.installation-video-banner__thumbnail');
                  if (thumbnail) thumbnail.src = this.getYouTubeThumbnailUrl(videoUrl);
                  banner.removeAttribute('hidden');
              } else {
                  banner.setAttribute('hidden', '');
                  banner.dataset.installationVideoUrl = '';
              }
          };

          if (variantVideos) {
              updateBannerForVariant();
          }

          this._handleVariantSelectsChanged = (event) => {
              if (variantVideos) {
                  const eventVariantId = event.detail?.variant?.id;
                  updateBannerForVariant(eventVariantId ? String(eventVariantId) : null);
              }
          };

          this._handleVariantChanged = (event) => {
              const banner = document.getElementById(`InstallationBanner-${sectionId}`);
              if (!banner) return;

              if (variantVideos) {
                  updateBannerForVariant();
              } else {
                  banner.setAttribute('hidden', '');
                  banner.dataset.installationVideoUrl = '';
                  delete banner.dataset.hasDrawer;
                  const label = document.getElementById(`InstallationConnectionLabel-${sectionId}`);
                  if (label) label.textContent = '';
              }
              this.computeOOS();
          };

          this._handleDrawerClick = (event) => {
              this.updateInstallationVideoBanner(event.detail.optionElement);
          };

          document.addEventListener('variant-selects-changed', this._handleVariantSelectsChanged);
          document.addEventListener('variant:changed', this._handleVariantChanged);
          document.addEventListener('variant:drawer:click', this._handleDrawerClick);

          const changeTrigger = document.getElementById(`InstallationVideoChangeTrigger-${sectionId}`);
          if (changeTrigger) {
              changeTrigger.addEventListener('click', () => {
                  const drawerTrigger = this.querySelector('.drawer-trigger--visible');
                  if (drawerTrigger) drawerTrigger.click();
              });
          }

          const trigger = document.getElementById(`InstallationVideoTrigger-${sectionId}`);
          const modal = document.getElementById(`InstallationVideoModal-${sectionId}`);
          const iframe = document.getElementById(`InstallationVideoIframe-${sectionId}`);
          const banner = document.getElementById(`InstallationBanner-${sectionId}`);

          if (trigger && modal && iframe) {
              trigger.addEventListener('click', () => {
                  const videoUrl = banner?.dataset?.installationVideoUrl;
                  if (videoUrl) {
                      iframe.src = this.getYouTubeEmbedUrl(videoUrl);
                      modal.show(trigger);
                  }
              });

              const closeBtn = document.getElementById(`ModalClose-InstallationVideo-${sectionId}`);
              if (closeBtn) {
                  closeBtn.addEventListener('click', () => {
                      iframe.src = '';
                  });
              }

              modal.addEventListener('click', (e) => {
                  if (e.target === modal) iframe.src = '';
              });
          }
      }

      extractYouTubeVideoId(url) {
          try {
              const urlObj = new URL(url);
              if (urlObj.hostname === 'youtu.be') {
                  return urlObj.pathname.slice(1).split('?')[0];
              }
              if (urlObj.hostname.includes('youtube.com')) {
                  return urlObj.searchParams.get('v')
                      || urlObj.pathname.split('/').filter(Boolean).pop();
              }
              return '';
          } catch {
              return '';
          }
      }

      getYouTubeThumbnailUrl(url) {
          const videoId = this.extractYouTubeVideoId(url);
          if (!videoId) return '';
          return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
      }

      getYouTubeEmbedUrl(url) {
          try {
              const videoId = this.extractYouTubeVideoId(url);
              if (!videoId) return '';
              const startTime = new URL(url).searchParams.get('t') || 0;
              return `https://www.youtube.com/embed/${videoId}?start=${startTime}&autoplay=1&rel=0`;
          } catch {
              return '';
          }
      }


      isTerminalWithoutProduct(item, optionId) {
          if (item.dataset.bundleProduct) return false;
          return !this.restructured.querySelector(`[data-parent-id="${optionId}"]`);
      }

      applyNotAvailableInventory() {
          const sectionId = this.closest('[data-section]')?.dataset?.section;
          const label = document.getElementById(`I18nNotAvailable-${sectionId}`)?.textContent?.trim() || 'Not available';
          document.querySelectorAll('.pdp-start-inventory-level-container .inventory-level-container').forEach(el => {
              el.style.setProperty('--color', 'var(--color-red)');
              const textEl = el.querySelector('.inventory-level-text');
              if (textEl) textEl.textContent = label;
              const circle = el.querySelector('.inventory-level-container__circle');
              if (circle) circle.style.setProperty('--color', 'var(--color-red)');
          });
      }

      // Bundle PDPs render no #product-form-{sectionId} (only the installment
      // form), so the old id-scoped lookup matched nothing and the hidden input
      // kept its initial value — which BundleAddToCartButton falls back to.
      syncVariantIdInputs(variantId) {
          if (!variantId) return;
          const container = this.closest('.product') || document;
          container.querySelectorAll('input[name="id"]').forEach((input) => {
              if (input.value !== variantId) input.value = variantId;
          });
      }

      updateProductInfo(optionItem) {
        const sectionId = this.closest('[data-section]')?.dataset?.section
        const variantId = optionItem.dataset.bundleProduct
        const url = optionItem.dataset.bundleProductUrl
        const available = optionItem.dataset.bundleProductAvailable
        const showBackInStock = optionItem.dataset.bundleProductShowBackInStock === "true"

        if(!url || !variantId || !sectionId){
            return;
        }

        this.syncVariantIdInputs(variantId);

        this.updateBackInStockForm(optionItem);

        const parentBundleBtn = document.querySelector(`#product-form-${sectionId}`)?.closest('[data-section]')?.querySelector('bundle-add-to-cart-button')
            || document.querySelector('bundle-add-to-cart-button');
        const parentIsPreorder = parentBundleBtn?.getAttribute('data-is-preorder') === 'true';
        const preorder = parentIsPreorder || optionItem.dataset.bundlePreorder === "true";

        // Leaf clicks resolve the ATC. preorder = parentIsPreorder || leaf;
        // main product's preorder flag wins so badge + button stay consistent
        // with the main listing.
        if (optionItem.dataset.isLeaf === 'true') {
            document.dispatchEvent(new CustomEvent('bundle:variant-resolved', {
                detail: { resolved: true, variantId, available: available !== 'false', isPreorder: preorder },
            }));
        }

        fetch(
			`${url}?variant=${variantId}&section_id=${sectionId}`,
		)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, "text/html");
				const destination = document.getElementById(
					`price-${sectionId}`,
				);
				const source = html.getElementById(
					`price-${sectionId}`,
				);

                const inventoryDestinations = document.querySelectorAll(
                    `.pdp-start-inventory-level-container`,
                );
                const inventorySources = html.querySelectorAll(
                    `.pdp-start-inventory-level-container`,
                );

                if (source && destination) {
                    destination.innerHTML = source.innerHTML;
                    if (parentIsPreorder) {
                        const swappedBtn = destination.querySelector('bundle-add-to-cart-button');
                        if (swappedBtn) swappedBtn.setAttribute('data-is-preorder', 'true');
                        const swappedText = destination.querySelector('.add-bundle-product__text');
                        if (swappedText && window.variantStrings?.preorder) {
                            swappedText.textContent = window.variantStrings.preorder;
                        }
                    }
                }

                inventoryDestinations.forEach((inventoryDestination, index) => {
                    const inventorySource = inventorySources[index];
                    if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
                });

                // Main product preorder must win over leaf's inventory truth —
                // user wants badge + button consistent with main listing.
                if (parentIsPreorder) {
                    const preorderText = window.variantStrings?.inventoryPreorder || 'Preorder';
                    document.querySelectorAll('.pdp-start-inventory-level-container .inventory-level-container').forEach(el => {
                        el.style.setProperty('--color', 'orange');
                        const textEl = el.querySelector('.inventory-level-text');
                        if (textEl) textEl.textContent = preorderText;
                        const circle = el.querySelector('.inventory-level-container__circle');
                        if (circle) circle.style.setProperty('--color', 'orange');
                    });
                }

                const variantPicker = this.querySelector('variant-radios');
                if(variantPicker){
                    variantPicker.toggleAddButton(
                        available === "false",
                        window.variantStrings.soldOut,
                        false,
                        preorder ? window.variantStrings.preorder : null
                    );

                 }
			});
      }

      handleOptionClick(clickedItem) {
          const level = parseInt(clickedItem.getAttribute('data-level'), 10);
          const optionId = clickedItem.getAttribute('data-option-id');

          // Mark this column as user-advanced — applyCollapseState only collapses
          // columns the user has actually interacted with.
          const ownColumn = clickedItem.closest('.option-column, .pdp-step-column');
          if (ownColumn) ownColumn.dataset.userAdvanced = 'true';

          this.updateActiveState(clickedItem, level);
          this.updateInstallationVideoBanner(clickedItem);

          // Non-leaf clicks (intermediate steps) un-resolve the ATC button so
          // changing an earlier step disables it until a new leaf is picked.
          // Leaf clicks dispatch the resolved event from updateProductInfo.
          // A terminal tile (no children) without a bundle product is a
          // dead end — surface "Not available" instead of "Choose your options".
          if (clickedItem.dataset.isLeaf !== 'true') {
              const notAvailable = this.isTerminalWithoutProduct(clickedItem, optionId);
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false, notAvailable } }));
              if (notAvailable) this.applyNotAvailableInventory();
          }

          if (this.isDrawerOption(clickedItem)) {
              this.handleDrawerOption(clickedItem, level, optionId);
              // Automatically trigger the drawer open
              const nextColumn = this.getNextColumn(level);
              if (nextColumn) {
                    nextColumn.querySelectorAll('.drawer-trigger').forEach(trigger => {
                        const triggerOption = trigger.dataset.parentOption
                        trigger.style.display = triggerOption !== optionId ? 'none' : 'flex';
                        if(triggerOption === optionId){
                            trigger.click();
                        }
                    });
              }
              this.updateProductInfo(clickedItem);
              this.updateBackInStockForm(clickedItem);
          } else {
              this.handleRegularOption(level, optionId);
              this.updateProductInfo(clickedItem);
              // Update preorder attribute after regular option selection
              this.updatePreorderAttribute();
          }

          this.dispatchResolvedState(clickedItem);
          this.applyCollapseState();
      }

      dispatchResolvedState(clickedItem) {
          if (!clickedItem) {
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false } }));
              return;
          }
          if (clickedItem.dataset.isLeaf === 'true') {
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', {
                  detail: {
                      resolved: true,
                      variantId: clickedItem.dataset.bundleProduct || '',
                      available: clickedItem.dataset.bundleProductAvailable !== 'false',
                      isPreorder: clickedItem.dataset.bundlePreorder === 'true',
                  },
              }));
          } else {
              // Native tiles parent their children via data-variant-connection,
              // not data-parent-id — the terminal check would false-positive.
              const isNative = clickedItem.classList.contains('pdp-step-tile--native');
              const notAvailable = !isNative
                  && this.isTerminalWithoutProduct(clickedItem, clickedItem.getAttribute('data-option-id'));
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false, notAvailable } }));
              if (notAvailable) this.applyNotAvailableInventory();
          }
      }

      updateInstallationVideoBanner(clickedItem) {
          const sectionId = this.closest('[data-section]')?.dataset?.section;
          if (!sectionId) return;

          const videoUrl = clickedItem.dataset.installationVideoUrl;
          const isFromDrawer = !!clickedItem.closest('.variant-options-drawer');

          const connectionKey = (clickedItem.dataset.connectionKey || clickedItem.dataset.optionLabel || '').replace(/:$/, '').trim();
          const connectionValue = clickedItem.dataset.connectionValue || clickedItem.dataset.optionTitle || '';
          const connectionText = connectionKey ? `${connectionKey}: ${connectionValue}` : connectionValue;

          if (connectionText && isFromDrawer) {
              const groupBLabel = this.querySelector('.group-b-connection__text');
              if (groupBLabel) {
                  groupBLabel.textContent = connectionText;
                  groupBLabel.classList.remove('group-b-connection__text--unselected');
              }
              const groupBChangeBtn = this.querySelector('.group-b-connection__change-btn');
              if (groupBChangeBtn) groupBChangeBtn.style.display = '';
          }

          const banner = document.getElementById(`InstallationBanner-${sectionId}`);
          if (!banner) return;

          // Drawer selections still update the connection label, but the banner
          // itself only reveals when there's an actual video — a drawer pick
          // without installation_video_url otherwise shows an empty thumbnail
          // with a dead click target.
          if (isFromDrawer) {
              banner.dataset.hasDrawer = 'true';
              const label = document.getElementById(`InstallationConnectionLabel-${sectionId}`);
              if (label) label.textContent = connectionText;
          }

          if (videoUrl) {
              banner.removeAttribute('hidden');
              banner.dataset.installationVideoUrl = videoUrl;
              const thumbnail = banner.querySelector('.installation-video-banner__thumbnail');
              if (thumbnail) thumbnail.src = this.getYouTubeThumbnailUrl(videoUrl);

              const isDrawerParent = clickedItem.getAttribute('data-child-options-drawer') === 'true';
              banner.classList.toggle('installation-video-banner--variant-only', !isFromDrawer && !isDrawerParent);
          } else if (isFromDrawer && banner.dataset.installationVideoUrl) {
              // Drawer pick without its own video: keep the banner showing the
              // video inherited from the parent selection (e.g. the year tile).
              banner.removeAttribute('hidden');
          } else {
              banner.dataset.installationVideoUrl = '';
              banner.setAttribute('hidden', '');
          }
      }

      isDrawerOption(item) {
          return item.getAttribute('data-child-options-drawer') === 'true';
      }

      updateActiveState(clickedItem, level) {
          const subWrapper = clickedItem.closest('.pdp-step-sub-section__tiles');
          const ownWrapper = subWrapper
              || clickedItem.closest('.options-wrapper')
              || this.restructured.querySelector(`.option-column[data-level="${level}"] .options-wrapper`);
          if (ownWrapper) {
              ownWrapper.querySelectorAll('.option-item, .pdp-step-tile').forEach(item => {
                  item.classList.remove('active');
              });
          }

          // If user reclicked a HOST tile (not a sub-section tile), clear any
          // active state in the same column's sub-section — otherwise the old
          // Year selection caches even though it belongs to the previous host.
          if (!subWrapper) {
              const hostColumn = clickedItem.closest('.option-column');
              if (hostColumn) {
                  hostColumn.querySelectorAll('.pdp-step-sub-section .pdp-step-tile.active, .pdp-step-sub-section .option-item.active').forEach(t => t.classList.remove('active'));
              }
          }

         // Remove previous active options in the drawer
          const drawerActiveOptions = document.querySelectorAll(`.variant-options-drawer[data-parent-option="${clickedItem.dataset.optionId}"] .drawer-option-child.active`);
          drawerActiveOptions.length && drawerActiveOptions.forEach(option => option.classList.remove('active'));

          clickedItem.classList.add('active');
          
          // Also update preorder attribute based on final selected option
          this.updatePreorderAttribute();
      }
      
      getLastSelectedOption() {
          const columns = Array.from(this.restructured.querySelectorAll('.option-column'));
          if (!columns.length) return null;

          return columns.reduce((lastSelected, column) => {
              if (window.getComputedStyle(column).display !== 'none') {
                  const activeOption = column.querySelector('.option-item.active');
                  return activeOption || lastSelected;
              }
              return lastSelected;
          }, null);
      }
      
      updatePreorderAttribute() {
          const productSection = this.closest('[data-section]');
          if (!productSection) return;
          
          const initialBundleButton = productSection.querySelector('bundle-add-to-cart-button');
          if (!initialBundleButton) return;
          
          const isMainProductPreorder = initialBundleButton.getAttribute('data-is-preorder') === 'true';
          
          // Only update buttons within the current product context
          productSection.querySelectorAll('bundle-add-to-cart-button').forEach(btn => {
              if (isMainProductPreorder) {
                  btn.setAttribute('data-is-preorder', 'true');
              } else if (!btn._lastState?.isPreorder) {
                  // A leaf-resolved preorder owns the flag; don't clear it here.
                  btn.removeAttribute('data-is-preorder');
              }
          });
      }

      updateBackInStockForm(clickedItem) {
        // Only update forms within the current product context
        const productSection = this.closest('.product');
        if (!productSection) return;
        
        const backInStockForms = productSection.querySelectorAll('back-in-stock-form');
        const showBackInStock = clickedItem.dataset.bundleProductShowBackInStock === "true"
        const variantId = clickedItem.dataset.bundleProduct;
        backInStockForms.forEach(form => {
            form.style.display = showBackInStock ? 'block' : 'none';
            if(variantId){
                form.querySelector('input[name="variant_id"]').value = variantId;
            }
        });
      }

      handleDrawerOption(clickedItem, level, optionId) {
          const nextColumn = this.getNextColumn(level);
          if (!nextColumn) return;

          // Column-level visibility owned by applyCollapseState (--upcoming class).
          this.setupDrawer(nextColumn, optionId);
          if (nextColumn.dataset.inlineMobileMounted !== 'true') {
              this.hideChildOptions(nextColumn);
          }
      }

      getNextColumn(level) {
          return this.restructured.querySelector(`.option-column[data-level="${level + 1}"]`);
      }

      handleRegularOption(level, optionId) {
          const nextColumn = this.getNextColumn(level);
          if (!nextColumn) return;
          // Column-level visibility owned by applyCollapseState (--upcoming class).
          this.resetColumnState(nextColumn);


          // Hide all drawer triggers in the next column first
          const allDrawerTriggers = nextColumn.querySelectorAll('.drawer-trigger');
          allDrawerTriggers.forEach(trigger => {
              trigger.style.display = 'none';
              trigger.classList.remove('drawer-trigger--visible');
          });

          // Show all child options that belong to the selected parent
          const optionsWrapper = nextColumn.querySelector('.options-wrapper');
          if (optionsWrapper) {
              // Find the first child option to get the correct title. Prefer
              // non-drawer children, but fall back to drawer ones — branches
              // like Without-Heat-Pump have ONLY drawer children, and their
              // option_label is still the right column heading. Tiles render
              // as .pdp-step-tile (snippet-recursive-options-v2); .option-item
              // kept for legacy markup.
              const firstRegularChild = optionsWrapper.querySelector(
                  `.option-item[data-parent-id="${optionId}"]:not([data-child-options-drawer="true"]), `
                  + `.pdp-step-tile[data-parent-id="${optionId}"]:not([data-child-options-drawer="true"])`
              ) || optionsWrapper.querySelector(
                  `.option-item[data-parent-id="${optionId}"], .pdp-step-tile[data-parent-id="${optionId}"]`
              );

              // Update column title based on the regular child option
              const columnTitle = nextColumn.querySelector('.option-column-title');
              if (columnTitle && firstRegularChild) {
                  const label = firstRegularChild.getAttribute('data-option-label');
                  if (label) {
                      columnTitle.textContent = label;
                      // Phase 2: CSS handles visibility; remove stale inline style
                      columnTitle.style.removeProperty('display');
                  }
              }

              // Show all child options and their associated drawer triggers
              // Include pdp-step-tile as well as option-item (covers L2+ tiles)
              const childOptions = optionsWrapper.querySelectorAll(
                  `.option-item[data-parent-id="${optionId}"], .pdp-step-tile[data-parent-id="${optionId}"]`
              );
              childOptions.forEach(child => {
                  child.style.display = 'flex';

                  // If this is a drawer option, show its trigger. Column-level
                  // visibility is owned by applyCollapseState (--upcoming class).
                  if (child.getAttribute('data-child-options-drawer') === 'true') {
                      const childId = child.getAttribute('data-option-id');
                      const drawerTrigger = nextColumn.querySelector(`.drawer-trigger[data-parent-option="${childId}"]`);
                      if (drawerTrigger) {
                          drawerTrigger.removeAttribute('style');
                          drawerTrigger.classList.add('drawer-trigger--visible');
                      }
                  }
              });
          }

          this.hideDeeperColumns(level + 1);
          this.applyCollapseState();
      }

      resetColumnState(column) {
          // Hide all options first (option-item for L1+, pdp-step-tile for L2+)
          const options = column.querySelectorAll('.option-item, .pdp-step-tile:not(.pdp-step-tile--native)');
          options.forEach(option => option.style.display = 'none');

          // Reset drawer triggers
          const triggers = column.querySelectorAll('.drawer-trigger');
          triggers.forEach(trigger => {
              trigger.removeAttribute('style');
              trigger.classList.remove('drawer-trigger--visible');
          });

          // Phase 2: column title lives in the header; CSS handles its visibility
          // via the --complete class. Clear any stale inline display to let CSS win.
          const columnTitle = column.querySelector('.option-column-title');
          if (columnTitle) {
              columnTitle.style.removeProperty('display');
          }


          // Remove active states
          options.forEach(option => option.classList.remove('active'));

          // Reset group-b-connection drawer triggers to unselected state
          const sectionId = this.closest('[data-section]')?.dataset?.section;
          const i18n = this.getI18nStrings(sectionId);
          column.querySelectorAll('.group-b-connection').forEach(trigger => {
              const label = trigger.querySelector('.group-b-connection__text');
              if (label) {
                  label.textContent = i18n.chooseSelection;
                  label.classList.add('group-b-connection__text--unselected');
              }
              const changeBtn = trigger.querySelector('.group-b-connection__change-btn');
              if (changeBtn) changeBtn.style.display = 'none';
          });
      }

      hideDeeperColumns(startLevel) {
          // Only resets inner state (tiles, drawer triggers). Column-level
          // visibility is owned by applyCollapseState (--upcoming class).
          let currentLevel = startLevel + 1;
          while (true) {
              const deeperColumn = this.restructured.querySelector(`.option-column[data-level="${currentLevel}"]`);
              if (!deeperColumn) break;
              this.resetColumnState(deeperColumn);
              currentLevel++;
          }
      }

      hideChildOptions(nextColumn) {
          // Reset the column state
          this.resetColumnState(nextColumn);
      }

      updateInitialColumnTitles() {
          const allColumnTitles = this.restructured.querySelectorAll('.option-column-title');
          allColumnTitles.forEach(title => {
              title.style.removeProperty('display');
          });

          this.updateColumnTitlesForActiveVariant();

          const changeVariantDrawer = document.querySelector('change-variant-drawer-content');
          if (changeVariantDrawer) {
              changeVariantDrawer.handleVariantOptionChange();
          }
      }

      getActiveVariantValue() {
          const nativeActive = this.restructured?.querySelector('.pdp-step-tile--native.active');
          if (nativeActive?.dataset.nativeVariantValue) return nativeActive.dataset.nativeVariantValue;
          const checked = document.querySelector('variant-radios input[type="radio"]:checked');
          return checked?.value || null;
      }

      updateColumnTitlesForActiveVariant() {
          const variantValue = this.getActiveVariantValue();
          if (!variantValue) return;
          const cols = this.restructured.querySelectorAll(':scope > .option-column[data-level]');
          cols.forEach(col => {
              const depth = parseInt(col.dataset.level, 10);
              if (Number.isNaN(depth) || depth === 0) return;
              const titleEl = col.querySelector('.option-column-title');
              if (!titleEl) return;
              const tile = [...col.querySelectorAll('.option-item, .pdp-step-tile:not(.pdp-step-tile--native)')]
                  .find(t => t.getAttribute('data-variant-connection') === variantValue
                        && t.getAttribute('data-child-options-drawer') !== 'true');
              const label = tile?.getAttribute('data-option-label');
              if (label) titleEl.textContent = label;
          });
      }

      mountStepHelpPills() {
          // Build one pill per distinct step_help_id referenced by tiles in
          // each column. Pills start hidden; updateStepHelpPillsVisibility
          // toggles them based on currently-visible tiles so a pill doesn't
          // leak from one parent's subtree into another.
          const columns = this.restructured.querySelectorAll('.option-column, .pdp-step-column');
          const totalLevels = columns.length;

          columns.forEach((column) => {
              const level = parseInt(column.dataset.level, 10);
              const allTiles = column.querySelectorAll(
                  '.options-wrapper > .pdp-step-tile[data-step-help-id], '
                  + '.options-wrapper > .option-item[data-step-help-id], '
                  + '.pdp-step-tiles > .pdp-step-tile[data-step-help-id], '
                  + '.pdp-step-sub-section__tiles > .pdp-step-tile[data-step-help-id], '
                  + '.pdp-step-sub-section__tiles > .option-item[data-step-help-id]'
              );
              const seen = new Set();
              allTiles.forEach(tile => {
                  const stepHelpId = tile.dataset.stepHelpId;
                  if (!stepHelpId || seen.has(stepHelpId)) return;
                  seen.add(stepHelpId);

                  const template = this.querySelector(`template.step-help-template[data-step-help-id="${stepHelpId}"]`);
                  if (!template) return;

                  const pill = document.createElement('button');
                  pill.type = 'button';
                  pill.className = 'step-help-pill';
                  pill.setAttribute('aria-haspopup', 'dialog');
                  pill.dataset.stepHelpId = stepHelpId;
                  pill.hidden = true;
                  pill.innerHTML = `
                      <span class="step-help-pill__icon" aria-hidden="true"></span>
                      <span class="step-help-pill__label"></span>
                  `;
                  const label = template.content.querySelector('.step-help-drawer__title')?.textContent?.trim()
                      || column.querySelector('.option-column-title')?.textContent?.trim()
                      || '';
                  pill.querySelector('.step-help-pill__label').textContent = label;

                  const body = column.querySelector('.option-column__body-inner')
                      || column.querySelector('.option-column__body')
                      || column;
                  const optionsWrapper = body.querySelector('.options-wrapper') || column.querySelector('.options-wrapper');

                  const isDeepest = level === totalLevels - 1;
                  if (isDeepest) {
                      body.appendChild(pill);
                      pill.classList.add('step-help-pill--bottom');
                  } else {
                      body.insertBefore(pill, optionsWrapper);
                      pill.classList.add('step-help-pill--top');
                  }

                  pill.addEventListener('click', (e) => {
                      e.stopPropagation();
                      this.openStepHelp(stepHelpId, pill);
                  });
              });
          });

          this.updateStepHelpPillsVisibility();
      }

      updateStepHelpPillsVisibility() {
          // Show a pill only when one of its column's currently-visible tiles
          // references that step_help_id. Stops the pill from leaking across
          // sibling subtrees that don't define step_help.
          const columns = this.restructured.querySelectorAll('.option-column, .pdp-step-column');
          columns.forEach(column => {
              const pills = column.querySelectorAll('.step-help-pill');
              if (!pills.length) return;
              const visibleIds = new Set();
              column.querySelectorAll('[data-step-help-id]').forEach(tile => {
                  if (tile.classList.contains('step-help-pill')) return;
                  // Hidden if either the tile itself or an ancestor is
                  // collapsed. `offsetParent` is null in either case.
                  if (tile.offsetParent === null) return;
                  if (getComputedStyle(tile).display === 'none') return;
                  if (tile.hidden) return;
                  const id = tile.dataset.stepHelpId;
                  if (id) visibleIds.add(id);
              });
              pills.forEach(pill => {
                  pill.hidden = !visibleIds.has(pill.dataset.stepHelpId);
              });
          });
      }

      parseStepHelpContent(root) {
          if (!root || root.dataset.stepHelpParsed) return;
          root.dataset.stepHelpParsed = 'true';

          const paragraphs = [...root.querySelectorAll('p')];
          paragraphs.forEach((p) => {
              const html = p.innerHTML;
              const blocks = html
                  .split(/(?:<br\s*\/?>(?:\s|&nbsp;)*){2,}/i)
                  .map((b) => b.trim())
                  .filter(Boolean);
              if (blocks.length < 2 && !blocks.some((b) => /^\d+\.\s/.test(b))) return;

              const out = document.createDocumentFragment();
              let ol = null;
              blocks.forEach((block) => {
                  const m = block.match(/^(\d+)\.\s*([\s\S]*)$/);
                  if (m) {
                      if (!ol) { ol = document.createElement('ol'); out.appendChild(ol); }
                      const parts = m[2].split(/<br\s*\/?>/i);
                      const title = (parts.shift() || '').trim();
                      const bodyHtml = parts.join('<br>').trim().replace(/(?:<br\s*\/?>\s*)+$/i, '');
                      const li = document.createElement('li');
                      li.innerHTML = `<strong>${title}</strong>${bodyHtml ? `<p>${bodyHtml}</p>` : ''}`;
                      ol.appendChild(li);
                  } else {
                      ol = null;
                      const para = document.createElement('p');
                      para.innerHTML = block;
                      out.appendChild(para);
                  }
              });
              p.replaceWith(out);
          });
      }

      openStepHelp(stepHelpId, triggeringPill = null) {
          const template = this.querySelector(`template.step-help-template[data-step-help-id="${stepHelpId}"]`);
          if (!template) return;

          let overlay = document.getElementById('StepHelpOverlay');
          if (!overlay) {
              overlay = document.createElement('div');
              overlay.id = 'StepHelpOverlay';
              overlay.className = 'step-help-overlay';
              overlay.innerHTML = `
                  <div class="step-help-overlay__backdrop" data-close></div>
                  <button type="button" class="step-help-overlay__close" data-close aria-label="Close"></button>
                  <div class="step-help-overlay__panel">
                      <div class="step-help-overlay__body"></div>
                  </div>
              `;
              document.body.appendChild(overlay);
              overlay.addEventListener('click', (e) => {
                  if (e.target.matches('[data-close]')) {
                      overlay.removeAttribute('open');
                      this.unlockStepHelp(overlay);
                      const restoreTo = overlay._restoreFocusTo;
                      if (restoreTo && typeof restoreTo.focus === 'function') restoreTo.focus();
                      overlay._restoreFocusTo = null;
                  }
              });
              this.wireStepHelpDragDismiss(overlay);
          }

          const body = overlay.querySelector('.step-help-overlay__body');
          body.innerHTML = '';
          body.appendChild(template.content.cloneNode(true));

          this.parseStepHelpContent(body.querySelector('.step-help-drawer__content'));
          this.parseStepHelpContent(body.querySelector('.step-help-drawer__content-after'));

          const videoEl = body.querySelector('.step-help-drawer__video');
          if (videoEl) {
              const url = videoEl.dataset.youtubeUrl;
              const embed = this.getYouTubeEmbedUrl(url);
              if (embed) {
                  const iframe = document.createElement('iframe');
                  iframe.src = embed;
                  iframe.setAttribute('allowfullscreen', '');
                  iframe.setAttribute('frameborder', '0');
                  videoEl.appendChild(iframe);
              }
          }

          overlay._restoreFocusTo = triggeringPill;
          overlay.setAttribute('open', '');
          this.lockStepHelp(overlay);
          overlay.querySelector('.step-help-overlay__close')?.focus();
      }

      lockStepHelp(overlay) {
          if (overlay._scrollLocked) return;
          overlay._scrollLocked = true;
          window.__pdpScrollLock?.lock(overlay.querySelector('.step-help-overlay__body'));
      }

      unlockStepHelp(overlay) {
          if (!overlay._scrollLocked) return;
          overlay._scrollLocked = false;
          window.__pdpScrollLock?.unlock(overlay.querySelector('.step-help-overlay__body'));
      }

      wireStepHelpDragDismiss(overlay) {
          const panel = overlay.querySelector('.step-help-overlay__panel');
          if (!panel) return;
          const closeBtn = overlay.querySelector('.step-help-overlay__close');
          const scroller = overlay.querySelector('.step-help-overlay__body');
          const backdrop = overlay.querySelector('.step-help-overlay__backdrop');
          const movers = [panel, closeBtn].filter(Boolean);
          const isTouch = () => matchMedia('(hover: none)').matches || window.innerWidth <= 749;
          const CLOSE_DISTANCE = 110;
          const CLOSE_VELOCITY = 0.6;
          const TAP_SLOP = 6;
          const EASE_RETURN = 'cubic-bezier(0.22, 1, 0.36, 1)';
          const EASE_DISMISS = 'cubic-bezier(0.4, 0, 0.2, 1)';
          let startY = 0;
          let lastY = 0;
          let lastT = 0;
          let velocity = 0;
          let dragging = false;
          let primed = false;

          const setTransition = (v) => movers.forEach(el => { el.style.transition = v; });
          const setTransform = (v) => movers.forEach(el => { el.style.transform = v; });

          const clearTransform = () => {
              setTransition('');
              setTransform('');
              if (backdrop) backdrop.style.transition = '';
              overlay.style.removeProperty('--step-help-overlay-dim');
              panel.classList.remove('step-help-overlay__panel--dragging');
              panel.style.setProperty('--drag-progress', '0');
              dragging = false;
              primed = false;
          };

          panel.addEventListener('touchstart', (e) => {
              if (!isTouch()) return;
              if (scroller && scroller.scrollTop > 0) return;
              const t = e.touches[0];
              startY = lastY = t.clientY;
              lastT = performance.now();
              velocity = 0;
              primed = true;
              dragging = false;
          }, { passive: true });

          panel.addEventListener('touchmove', (e) => {
              if (!primed) return;
              const y = e.touches[0].clientY;
              const now = performance.now();
              const dy = y - startY;
              if (!dragging) {
                  if (dy > TAP_SLOP) {
                      dragging = true;
                      setTransition('none');
                      if (backdrop) backdrop.style.transition = 'none';
                      panel.classList.add('step-help-overlay__panel--dragging');
                  } else if (dy < -TAP_SLOP) {
                      primed = false;
                      return;
                  } else {
                      return;
                  }
              }
              const dt = Math.max(1, now - lastT);
              velocity = (y - lastY) / dt;
              lastY = y;
              lastT = now;

              const raw = Math.max(0, dy);
              const offset = raw <= 240 ? raw : 240 + (raw - 240) * 0.45;
              setTransform(`translate3d(0, ${offset}px, 0)`);
              const fade = Math.max(0, 1 - offset / 360);
              overlay.style.setProperty('--step-help-overlay-dim', String(fade));
              panel.style.setProperty('--drag-progress', String(Math.min(1, raw / CLOSE_DISTANCE)));
              if (offset > 0 && e.cancelable) e.preventDefault();
          }, { passive: false });

          const endHandler = (e) => {
              if (!primed) return;
              if (!dragging) { clearTransform(); return; }
              const dy = Math.max(0, (e.changedTouches?.[0]?.clientY ?? startY) - startY);
              const shouldClose = dy >= CLOSE_DISTANCE || velocity >= CLOSE_VELOCITY;
              // Re-enable the handle transition and straighten it as the panel resolves.
              panel.classList.remove('step-help-overlay__panel--dragging');
              panel.style.setProperty('--drag-progress', '0');

              if (shouldClose) {
                  const viewportH = window.innerHeight;
                  const remaining = Math.max(0, viewportH - dy);
                  const speed = Math.max(velocity, 1.4);
                  const duration = Math.min(360, Math.max(180, remaining / speed));
                  setTransition(`transform ${duration}ms ${EASE_DISMISS}`);
                  if (backdrop) backdrop.style.transition = `opacity ${duration}ms ${EASE_DISMISS}`;
                  setTransform(`translate3d(0, ${viewportH}px, 0)`);
                  overlay.style.setProperty('--step-help-overlay-dim', '0');
                  let fired = false;
                  const onDone = () => {
                      if (fired) return;
                      fired = true;
                      panel.removeEventListener('transitionend', onDone);
                      overlay.removeAttribute('open');
                      this.unlockStepHelp(overlay);
                      const restoreTo = overlay._restoreFocusTo;
                      if (restoreTo && typeof restoreTo.focus === 'function') restoreTo.focus();
                      overlay._restoreFocusTo = null;
                      clearTransform();
                  };
                  panel.addEventListener('transitionend', onDone);
                  setTimeout(onDone, duration + 80);
              } else {
                  setTransition(`transform 320ms ${EASE_RETURN}`);
                  if (backdrop) backdrop.style.transition = `opacity 220ms ${EASE_RETURN}`;
                  setTransform('translate3d(0, 0, 0)');
                  overlay.style.setProperty('--step-help-overlay-dim', '1');
                  let fired = false;
                  const onSnap = () => {
                      if (fired) return;
                      fired = true;
                      panel.removeEventListener('transitionend', onSnap);
                      clearTransform();
                  };
                  panel.addEventListener('transitionend', onSnap);
                  setTimeout(onSnap, 360);
              }
          };
          panel.addEventListener('touchend', endHandler);
          panel.addEventListener('touchcancel', endHandler);
      }

      activateNativeTile(tile) {
          const column = tile.closest('.pdp-step-column, .option-column');
          if (column) {
              column.querySelectorAll('.pdp-step-tile.active, .option-item.active').forEach(t => t.classList.remove('active'));
              column.dataset.userAdvanced = 'true';
          }

          // Native variant change wipes any resolved leaf. For products
          // without nested bundle_options, the native tile IS the leaf — emit
          // resolved with its variant info so ATC enables directly.
          if (tile.dataset.isLeaf === 'true') {
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', {
                  detail: {
                      resolved: true,
                      variantId: tile.dataset.bundleProduct,
                      available: tile.dataset.bundleProductAvailable !== 'false',
                      isPreorder: tile.dataset.bundlePreorder === 'true',
                  },
              }));
          } else {
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false } }));
          }

          const variantId = tile.dataset.nativeVariantId;
          const sectionId = this.closest('[data-section]')?.dataset?.section;
          const formInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
          if (formInput && variantId) {
              formInput.value = variantId;
              formInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const variantValue = tile.dataset.nativeVariantValue;
          if (variantValue) {
              // Uncheck non-matching radios BEFORE dispatching change on the
              // match. With multiple native option groups, a later group's
              // default-checked radio would otherwise still be checked when the
              // change handler reads getSelectedVariant() (last checked wins),
              // breaking the data-variant-connection reveal for level-1 tiles.
              const radios = document.querySelectorAll('variant-radios input[type="radio"]');
              radios.forEach(input => {
                  if (input.value !== variantValue) input.checked = false;
              });
              radios.forEach(input => {
                  if (input.value === variantValue) {
                      input.checked = true;
                      input.dispatchEvent(new Event('change', { bubbles: true }));
                  }
              });
          }

          // Re-apply active + tile visibility AFTER legacy reset wipes them.
          // Column-level visibility is owned by applyCollapseState (--upcoming).
          tile.classList.add('active');
          tile.style.display = 'flex';
          if (column) {
              column.querySelectorAll('.pdp-step-tile--native').forEach(t => { t.style.display = 'flex'; });
          }

          this.updateColumnTitlesForActiveVariant();
          this.dispatchResolvedState(tile);
          this.applyCollapseState();
      }

      applyCollapseState() {
          const columns = [...this.restructured.querySelectorAll('.option-column, .pdp-step-column')];
          columns.forEach((column) => {
              const active = column.querySelector('.options-wrapper .option-item.active, .options-wrapper .pdp-step-tile.active, .pdp-step-tiles .pdp-step-tile.active');

              // Only collapse a column when the user has actually advanced past it
              // (clicked one of its tiles). Liquid's default-selected `active` class
              // alone is NOT enough — initial load keeps Step 1 fully expanded.
              // We don't gate on the NEXT column being visible because future columns
              // are hidden via --upcoming until this one is marked complete — that
              // would create a deadlock.
              const userAdvanced = column.dataset.userAdvanced === 'true';
              const sub = column.querySelector('.pdp-step-sub-section');
              const subActive = sub && sub.querySelector('.pdp-step-sub-section__tiles .pdp-step-tile.active, .pdp-step-sub-section__tiles .option-item.active');
              let subRequired = false;
              if (sub && active) {
                  const activeId = active.dataset.optionId;
                  const applicableTiles = [...sub.querySelectorAll('.pdp-step-sub-section__tiles > .pdp-step-tile, .pdp-step-sub-section__tiles > .option-item')]
                      .filter(t => !t.dataset.parentId || t.dataset.parentId === activeId);
                  subRequired = applicableTiles.length > 0;
              }
              const subResolved = !subRequired || !!subActive;
              const shouldCollapse = !!active && userAdvanced && subResolved;
              column.classList.toggle('option-column--complete', shouldCollapse);
              column.classList.toggle('pdp-step-column--complete', shouldCollapse);

              const badge = column.querySelector('[data-role="badge"]');
              if (badge) {
                  if (shouldCollapse) {
                      badge.textContent = '' 
                      badge.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none"><path d="M14.7863 0.937511L14.0917 0.221286C13.8056 -0.073762 13.3384 -0.073762 13.0498 0.221286L5.76645 7.7341L1.9511 3.79991C1.66497 3.50482 1.19782 3.50482 0.909221 3.79991L0.214601 4.51613C-0.0715337 4.81121 -0.0715337 5.29288 0.214601 5.59047L5.24306 10.7755C5.38737 10.9243 5.57322 11 5.76156 11C5.94989 11 6.1382 10.9243 6.28008 10.7755L14.779 2.01188C15.0724 1.71179 15.0724 1.23262 14.7863 0.937511Z" fill="white"/></svg>';
                      badge.classList.add('option-column__badge--complete');
                  } else {
                      badge.textContent = String(parseInt(column.dataset.level, 10) + 1);
                      badge.classList.remove('option-column__badge--complete');
                  }
              }

              const summary = column.querySelector('.option-column__summary');
              const summaryText = summary?.querySelector('.option-column__summary-text');
              const summaryThumb = summary?.querySelector('.option-column__summary-thumb');
              if (summary && summaryText) {
                  summary.hidden = !shouldCollapse;
                  const subActiveTile = column.querySelector('.pdp-step-sub-section__tiles .pdp-step-tile.active, .pdp-step-sub-section__tiles .option-item.active');
                  const baseTitle = active?.dataset.optionTitle || '';
                  const subTitle = subActiveTile?.dataset.optionTitle || '';
                  summaryText.textContent = subTitle ? `${baseTitle} | ${subTitle}` : baseTitle;
                  if (summaryThumb) {
                      summaryThumb.innerHTML = '';
                      const sourceImg = active?.querySelector('.option-title-image-wrapper img, .pdp-step-tile__thumb img');
                      if (sourceImg) {
                          const clone = sourceImg.cloneNode(true);
                          clone.removeAttribute('loading');
                          summaryThumb.appendChild(clone);
                      }
                  }
              }
          });

          columns.forEach((column) => {
              const sub = column.querySelector('.pdp-step-sub-section');
              if (!sub) return;
              const hostActive = column.querySelector('.options-wrapper > .pdp-step-tile.active, .options-wrapper > .option-item.active');
              if (!hostActive) {
                  sub.toggleAttribute('hidden', true);
                  return;
              }
              const activeId = hostActive.dataset.optionId;
              let anyVisible = false;
              sub.querySelectorAll('.pdp-step-sub-section__tiles > .pdp-step-tile, .pdp-step-sub-section__tiles > .option-item').forEach(t => {
                  const pid = t.dataset.parentId;
                  const matches = !pid || pid === activeId;
                  t.style.display = matches ? '' : 'none';
                  if (matches) anyVisible = true;
              });
              sub.toggleAttribute('hidden', !anyVisible);
          });

          let pastCurrent = false;
          let chainTerminated = false;
          // setupDrawer pins inline max-height/overflow on drawer-host columns;
          // those must be cleared when a column collapses or the --upcoming
          // max-height:0 loses to the inline style and ghost space remains.
          const hideUpcoming = (column) => {
              column.classList.add('option-column--upcoming');
              column.style.removeProperty('max-height');
              column.style.removeProperty('overflow');
          };
          columns.forEach((column) => {
              if (chainTerminated) {
                  hideUpcoming(column);
                  return;
              }
              const isComplete = column.classList.contains('option-column--complete')
                  || column.classList.contains('pdp-step-column--complete');
              if (isComplete) {
                  column.classList.remove('option-column--upcoming');
                  const leafActive = column.querySelector(
                      '.options-wrapper .pdp-step-tile.active[data-is-leaf="true"], '
                      + '.options-wrapper .option-item.active[data-is-leaf="true"], '
                      + '.pdp-step-sub-section .pdp-step-tile.active[data-is-leaf="true"], '
                      + '.pdp-step-sub-section .option-item.active[data-is-leaf="true"]'
                  );
                  if (leafActive) {
                      chainTerminated = true;
                      return;
                  }
                  const activeTile = column.querySelector(
                      '.options-wrapper .pdp-step-tile.active, '
                      + '.options-wrapper .option-item.active, '
                      + '.pdp-step-sub-section .pdp-step-tile.active, '
                      + '.pdp-step-sub-section .option-item.active'
                  );
                  // A terminal tile with no bundle product is a dead end —
                  // terminate the chain so deeper columns stay hidden instead
                  // of revealing another path's step (e.g. "Connection Type").
                  if (activeTile
                      && !activeTile.classList.contains('pdp-step-tile--native')
                      && this.isTerminalWithoutProduct(activeTile, activeTile.dataset.optionId)) {
                      chainTerminated = true;
                      return;
                  }
                  if (activeTile && activeTile.dataset.bundleProduct) {
                      const myLevel = parseInt(column.dataset.level, 10);
                      const myOptId = activeTile.dataset.optionId;
                      const nextCol = columns.find(c => parseInt(c.dataset.level, 10) === myLevel + 1);
                      const hasChildren = nextCol && nextCol.querySelector(
                          `.option-item[data-parent-id="${myOptId}"], .pdp-step-tile[data-parent-id="${myOptId}"]`
                      );
                      if (!hasChildren) {
                          chainTerminated = true;
                          document.dispatchEvent(new CustomEvent('bundle:variant-resolved', {
                              detail: {
                                  resolved: true,
                                  variantId: activeTile.dataset.bundleProduct,
                                  available: activeTile.dataset.bundleProductAvailable !== 'false',
                                  isPreorder: activeTile.dataset.bundlePreorder === 'true',
                              },
                          }));
                      }
                  }
                  return;
              }
              if (pastCurrent) {
                  hideUpcoming(column);
              } else {
                  column.classList.remove('option-column--upcoming');
                  pastCurrent = true;
              }
          });

          this.updateStepHelpPillsVisibility?.();
      }

      computeOOS() {
          const allItems = [...this.restructured.querySelectorAll('.option-item, .pdp-step-tile')];
          const byParent = new Map();
          allItems.forEach((item) => {
              const pid = item.dataset.parentId || null;
              if (!byParent.has(pid)) byParent.set(pid, []);
              byParent.get(pid).push(item);
          });

          const memo = new Map();
          const available = (item) => {
              if (memo.has(item)) return memo.get(item);
              const ownAvail = item.dataset.bundleProductAvailable;
              const children = byParent.get(item.dataset.optionId) || [];
              let result;
              if (children.length === 0) {
                  result = ownAvail !== 'false';
              } else {
                  result = children.some((c) => available(c));
              }
              memo.set(item, result);
              return result;
          };

          // An unavailable subtree is "not applicable" (product not offered for
          // this fitment) when every dead end in it is a no-variant node, as
          // opposed to a real variant that is sold out.
          const naMemo = new Map();
          const notApplicable = (item) => {
              if (naMemo.has(item)) return naMemo.get(item);
              const children = byParent.get(item.dataset.optionId) || [];
              let result;
              if (children.length === 0) {
                  result = item.dataset.notApplicable === 'true';
              } else {
                  result = children.every((c) => available(c) || notApplicable(c));
              }
              naMemo.set(item, result);
              return result;
          };

          const sectionId = this.closest('[data-section]')?.dataset?.section;
          const i18n = this.getI18nStrings(sectionId);

          allItems.forEach((item) => {
              const isAvail = available(item);
              item.classList.toggle('option-item--oos', !isAvail);
              if (!isAvail && !item.querySelector('.option-item__oos-chip')) {
                  const chip = document.createElement('span');
                  chip.className = 'option-item__oos-chip';
                  chip.textContent = notApplicable(item) ? i18n.notAvailable : i18n.outOfStock;
                  item.appendChild(chip);
              } else if (isAvail) {
                  item.querySelector('.option-item__oos-chip')?.remove();
              }
          });
      }

      showErrorMessage(message) {
          // Create banner container
          const banner = document.createElement('div');
          banner.classList.add('banner', 'banner--error');

          // Create banner content
          const bannerContent = document.createElement('div');
          bannerContent.classList.add('banner__content');

          // Add message
          const messageText = document.createElement('p');
          messageText.classList.add('banner__message');
          messageText.textContent = message;

          // Add close button
          const closeButton = document.createElement('button');
          closeButton.classList.add('banner__close');
          closeButton.setAttribute('aria-label', 'Close');
          closeButton.innerHTML = '×';
          closeButton.onclick = () => banner.remove();

          // Assemble banner
          bannerContent.appendChild(messageText);
          bannerContent.appendChild(closeButton);
          banner.appendChild(bannerContent);

          // Add to document
          document.body.appendChild(banner);

          // Auto-remove after 5 seconds
          setTimeout(() => {
              if (banner.parentNode) {
                  banner?.remove();
              }
          }, 5000);
      }

      setupDrawer(nextColumn, optionId) {
          // The column title at this depth is shared across paths and may
          // carry another subtree's label — overwrite with the drawer
          // children's own option_label (same fix as the in-drawer title).
          const drawerColumnLabel = this.drawerChildLabel(optionId);
          if (drawerColumnLabel) {
              const titleEl = nextColumn.querySelector('.option-column-title');
              if (titleEl) titleEl.textContent = drawerColumnLabel;
          }

          if (window.matchMedia('(max-width: 989px)').matches) {
              this.setupInlineMobile(nextColumn, optionId);
              return;
          }

          // Reset column state first
          this.resetColumnState(nextColumn);

          // Look for a specific trigger for this option
          let trigger = nextColumn.querySelector(`.drawer-trigger[data-parent-option="${optionId}"]`);

          if (!trigger) {
              trigger = this.createDrawerTrigger(nextColumn, optionId);
          } else {
              trigger.removeAttribute('style');
          }

          // Use classList to control visibility instead of inline styles
          trigger.classList.add('drawer-trigger--visible');

          nextColumn.style.maxHeight = '400px';
          nextColumn.style.overflow = 'visible';
      }

      setupInlineMobile(nextColumn, optionId) {
          // One-line: viewport switch mid-session shows stale state; reload to pick up new mode.
          if (nextColumn.dataset.inlineMobileMounted === 'true') {
              const activeChild = nextColumn.querySelector(
                  `.option-item[data-parent-id="${optionId}"].active, .pdp-step-tile[data-parent-id="${optionId}"].active`
              );
              const activeId = activeChild?.getAttribute('data-option-id');
              nextColumn.querySelectorAll('.inline-drawer-content__panel').forEach(p => {
                  if (activeId && p.getAttribute('data-content-for') === activeId) {
                      p.removeAttribute('hidden');
                  } else {
                      p.setAttribute('hidden', '');
                  }
              });
              return;
          }

          const childItems = nextColumn.querySelectorAll(
              `.option-item[data-parent-id="${optionId}"], .pdp-step-tile[data-parent-id="${optionId}"]`
          );

          childItems.forEach(child => {
              child.style.removeProperty('display');
          });

          const container = document.createElement('div');
          container.classList.add('inline-drawer-content');
          container.setAttribute('data-parent-option', optionId);

          childItems.forEach(child => {
              const childId = child.getAttribute('data-option-id');
              if (!childId) return;

              const originalContent = child.querySelector('.drawer-content-option');
              if (!originalContent) return;

              const panel = document.createElement('div');
              panel.classList.add('inline-drawer-content__panel');
              panel.setAttribute('data-content-for', childId);
              panel.setAttribute('hidden', '');

              const inner = originalContent.querySelector('.drawer-option-content');
              if (inner) {
                  const cloned = inner.cloneNode(true);
                  const iframe = cloned.querySelector('iframe[src]');
                  if (iframe) {
                      iframe.setAttribute('data-src', iframe.getAttribute('src'));
                      iframe.removeAttribute('src');
                  }
                  panel.appendChild(cloned);
              }

              container.appendChild(panel);
          });

          nextColumn.appendChild(container);

          nextColumn.addEventListener('click', (e) => {
              const clickedChild = e.target.closest(
                  `.option-item[data-parent-id="${optionId}"], .pdp-step-tile[data-parent-id="${optionId}"]`
              );
              if (!clickedChild) return;

              const itemId = clickedChild.getAttribute('data-option-id');

              container.querySelectorAll('.inline-drawer-content__panel').forEach(p => p.setAttribute('hidden', ''));

              const targetPanel = container.querySelector(`.inline-drawer-content__panel[data-content-for="${itemId}"]`);
              if (targetPanel) {
                  targetPanel.removeAttribute('hidden');
                  const lazyIframe = targetPanel.querySelector('iframe[data-src]');
                  if (lazyIframe) {
                      lazyIframe.setAttribute('src', lazyIframe.getAttribute('data-src'));
                      lazyIframe.removeAttribute('data-src');
                  }
              }

              nextColumn.querySelectorAll(
                  `.option-item[data-parent-id="${optionId}"], .pdp-step-tile[data-parent-id="${optionId}"]`
              ).forEach(item => item.classList.remove('active'));
              clickedChild.classList.add('active');

              const mainItem = this.restructured.querySelector(`.option-item[data-option-id="${itemId}"]`);
              if (mainItem) {
                  const level = parseInt(mainItem.getAttribute('data-level'), 10);
                  this.updateActiveState(mainItem, level);
                  this.handleRegularOption(level, itemId);
                  this.updateProductInfo(mainItem);
              }

              this.updatePreorderAttribute();

              document.dispatchEvent(new CustomEvent('variant:drawer:click', { detail: { optionElement: clickedChild } }));
          });

          nextColumn.dataset.inlineMobileMounted = 'true';
      }

      getI18nStrings(sectionId) {
          const banner = document.getElementById(`InstallationBanner-${sectionId}`);
          return {
              chooseSelection: banner?.dataset?.i18nChooseSelection || 'Choose Selection',
              change: banner?.dataset?.i18nChange || 'Change',
          };
      }

      createDrawerTrigger(nextColumn, optionId) {
          const sectionId = this.closest('[data-section]')?.dataset?.section;
          const i18n = this.getI18nStrings(sectionId);
          const trigger = document.createElement('button');
          trigger.type = 'button';
          trigger.classList.add('drawer-trigger');
          trigger.textContent = this.drawerTriggerLabel(nextColumn, i18n, optionId);
          trigger.setAttribute('aria-controls', `VariantOptionsDrawer-${optionId}`);
          trigger.setAttribute('data-parent-option', optionId);
          // Mount inside the column header next to the title (per design); the
          // click-row re-expand handler ignores clicks on .drawer-trigger.
          const header = nextColumn.querySelector('.option-column__header');
          (header || nextColumn).appendChild(trigger);

          this.initializeDrawer(optionId);
          this.setupDrawerEvents(trigger, nextColumn, optionId);

          return trigger;
      }

      getI18nStrings(sectionId) {
          return {
              chooseSelection: document.getElementById(`I18nChooseSelection-${sectionId}`)?.textContent?.trim() || '',
              selectPrefix:    document.getElementById(`I18nSelectPrefix-${sectionId}`)?.textContent?.trim()    || 'Select',
              outOfStock:      document.getElementById(`I18nOutOfStock-${sectionId}`)?.textContent?.trim()    || 'Out of stock',
              notAvailable:    document.getElementById(`I18nNotAvailable-${sectionId}`)?.textContent?.trim() || 'Not available',
              change:          document.getElementById(`I18nChange-${sectionId}`)?.textContent?.trim()       || 'Change',
          };
      }

      drawerChildLabel(optionId) {
          // The drawer's own children carry the correct option_label. The
          // column title can't be trusted here — it's shared across every
          // path at that depth and reflects whichever path was built first.
          return this.restructured.querySelector(
              `[data-parent-id="${optionId}"][data-option-label]`
          )?.dataset.optionLabel?.trim() || '';
      }

      drawerTriggerLabel(nextColumn, i18n, optionId) {
          const childLabel = optionId ? this.drawerChildLabel(optionId) : '';
          const titleText = childLabel || nextColumn.querySelector('.option-column-title')?.textContent?.trim() || '';
          const cleaned = titleText.replace(/:\s*$/, '').trim();
          if (!cleaned) return i18n.chooseSelection;
          // Labels authored as "Select your car's build period" already carry
          // the verb — don't produce "Select Select ...".
          const lower = cleaned.toLowerCase();
          const prefixLower = i18n.selectPrefix.toLowerCase();
          return lower.startsWith(prefixLower + ' ') ? cleaned : `${i18n.selectPrefix} ${cleaned}`;
      }

      createDrawerTriggerGroupB(nextColumn, optionId) {
          const sectionId = this.closest('[data-section]')?.dataset?.section;
          const i18n = this.getI18nStrings(sectionId);

          const wrapper = document.createElement('div');
          wrapper.classList.add('drawer-trigger', 'group-b-connection');
          wrapper.setAttribute('aria-controls', `VariantOptionsDrawer-${optionId}`);
          wrapper.setAttribute('data-parent-option', optionId);

          const label = document.createElement('p');
          label.classList.add('group-b-connection__text', 'group-b-connection__text--unselected');
          label.textContent = this.drawerTriggerLabel(nextColumn, i18n, optionId);

          const changeBtn = document.createElement('button');
          changeBtn.classList.add('group-b-connection__change-btn');
          changeBtn.textContent = i18n.change;
          changeBtn.type = 'button';
          changeBtn.style.display = 'none';

          wrapper.appendChild(label);
          wrapper.appendChild(changeBtn);
          const header = nextColumn.querySelector('.option-column__header');
          (header || nextColumn).appendChild(wrapper);

          this.initializeDrawer(optionId);
          this.setupDrawerEvents(wrapper, nextColumn, optionId);

          return wrapper;
      }

      handleSubmitButtonClick(drawerId) {
          const drawer = document.getElementById(drawerId);
          this.closeDrawer(drawer);
      }

      convertTileToOptionItem(el) {
          if (!el.classList.contains('pdp-step-tile')) return;
          el.classList.remove('pdp-step-tile');
          el.classList.add('option-item');

          const text = el.querySelector(':scope > .pdp-step-tile__text');
          const titleText = text?.querySelector('.pdp-step-tile__title')?.textContent?.trim() || el.dataset.optionTitle || '';
          const helperText = text?.querySelector('.pdp-step-tile__helper')?.textContent?.trim() || '';
          text?.remove();

          const thumb = el.querySelector(':scope > .pdp-step-tile__thumb');
          if (thumb) {
              thumb.className = 'option-title-image-wrapper';
          }

          const drawerContent = el.querySelector(':scope > .drawer-content-option');

          while (el.firstChild) el.removeChild(el.firstChild);

          el.appendChild(document.createTextNode(titleText));
          if (helperText) {
              const helperEl = document.createElement('div');
              helperEl.className = 'option-item__helper-text';
              helperEl.textContent = helperText;
              el.appendChild(helperEl);
          }
          if (thumb) el.appendChild(thumb);
          if (drawerContent) el.appendChild(drawerContent);
      }

      initializeDrawer(optionId) {
          const drawerId = `VariantOptionsDrawer-${optionId}`;
          if (!document.getElementById(drawerId)) {
              // Create overlay if it doesn't exist
              if (!document.querySelector('.drawer-overlay')) {
                  const overlay = document.createElement('div');
                  overlay.classList.add('drawer-overlay');
                  overlay.addEventListener('click', () => {
                      const activeDrawer = document.querySelector('.variant-options-drawer.is-active');
                      if (activeDrawer) {
                          this.closeDrawer(activeDrawer);
                      }
                  });
                  document.body.appendChild(overlay);
              }

              const drawer = document.createElement('div');
              drawer.id = drawerId;
              drawer.classList.add('variant-options-drawer');
              drawer.setAttribute('data-parent-option', optionId);

              // Force initial position
              drawer.style.transition = 'none';
              drawer.style.right = '-100%';

              // Create inner container
              const innerContainer = document.createElement('div');
              innerContainer.classList.add('inner-container-drawer');

              // Create header container
              const headerContainer = document.createElement('div');
              headerContainer.classList.add('header-drawer-option');

              // Header shows the breadcrumb of the selected path. Refreshed on
              // every openDrawer() — the drawer element is cached per parent
              // option, so text set only at creation would go stale.
              const modelText = document.createElement('div');
              modelText.classList.add('header-drawer-option__title');
              modelText.textContent = this.buildDrawerBreadcrumb(optionId);
              const parentOption = this.restructured.querySelector(`.option-item[data-option-id="${optionId}"], .pdp-step-tile[data-option-id="${optionId}"]`);

              // Create close button with bound event handler
              const closeButton = document.createElement('button');
              closeButton.classList.add('variant-options-drawer__close');
              closeButton.innerHTML = '×';
              closeButton.addEventListener('click', () => this.closeDrawer(drawer));

              // Add model text and close button to header
              headerContainer.appendChild(modelText);
              headerContainer.appendChild(closeButton);

              // Create content container
              const contentContainer = document.createElement('div');
              contentContainer.classList.add('content-drawer');

              // Add title for drawer options — prefer the drawer children's own
              // option_label; the next column's title is shared across paths
              // at that depth and may belong to a different subtree.
              const drawerOptionsTitle = document.createElement('div');
              drawerOptionsTitle.classList.add('option-column-title');
              const childLabel = this.drawerChildLabel(optionId);
              if (childLabel) {
                  drawerOptionsTitle.textContent = childLabel;
              } else {
                  const nextColumn = this.restructured.querySelector(`.option-column[data-level="${parseInt(parentOption?.getAttribute('data-level'), 10) + 1}"]`);
                  const titleText = nextColumn?.querySelector('.option-column-title')?.textContent;
                  if (titleText) {
                      drawerOptionsTitle.textContent = titleText;
                  }
              }
              contentContainer.appendChild(drawerOptionsTitle);

              // Create options container
              const drawerOptions = document.createElement('div');
              drawerOptions.classList.add('variant-options-drawer__options');

              // Add Submit and continue button
              const submitButton = document.createElement('button');
              submitButton.classList.add('drawer-submit-button');
              submitButton.textContent = 'Submit and continue';

              // Add click event listener to close drawer
              submitButton.addEventListener('click', () => {
                  const drawer = document.getElementById(drawerId);
                  this.closeDrawer(drawer);
              });

              const drawerContentArea = document.createElement('div');
              drawerContentArea.classList.add('variant-options-drawer__content-area');

              // Structure the elements
              contentContainer.appendChild(drawerOptions);
              contentContainer.appendChild(submitButton); // Add the submit button after options
              contentContainer.appendChild(drawerContentArea);

              innerContainer.appendChild(headerContainer); // Add header first
              innerContainer.appendChild(contentContainer); // Then content
              drawer.appendChild(innerContainer);
              document.body.appendChild(drawer);

              // Force a reflow to ensure the initial position is applied
              drawer.offsetHeight;

              // Reset transition and remove inline style
              requestAnimationFrame(() => {
                  drawer.style.transition = '';
                  drawer.style.right = '';
              });
          }
      }

      setupDrawerEvents(trigger, nextColumn, optionId) {
          trigger.addEventListener('click', (e) => {
              if (trigger.classList.contains('group-b-connection')) {
                  const changeBtn = trigger.querySelector('.group-b-connection__change-btn');
                  if (changeBtn && changeBtn.style.display !== 'none' && !changeBtn.contains(e.target)) {
                      return;
                  }
              }
              const drawerId = `VariantOptionsDrawer-${optionId}`;
              const drawer = document.getElementById(drawerId);

              const allDrawers = document.querySelectorAll('.variant-options-drawer');
              allDrawers.forEach(d => {
                  if (d.id !== drawerId) {
                      d.classList.remove('is-active');
                  }
              });

              if (drawer.classList.contains('is-active')) {
                  this.closeDrawer(drawer);
                  return;
              }

              const drawerOptions = drawer.querySelector('.variant-options-drawer__options');

              if (!drawerOptions.hasChildNodes()) {
                  const drawerContentArea = drawer.querySelector('.variant-options-drawer__content-area');
                  const childItems = nextColumn.querySelectorAll(`.option-item[data-parent-id="${optionId}"], .pdp-step-tile[data-parent-id="${optionId}"]`);

                  childItems.forEach(child => {
                      const clone = child.cloneNode(true);
                      // Convert .pdp-step-tile markup to .option-item structure
                      // so the existing drawer CSS (which targets .option-item
                      // and .option-title-image-wrapper) styles them the same
                      // way as the live drawer on enhauto.com.
                      this.convertTileToOptionItem(clone);
                      const drawerContentEl = clone.querySelector('.drawer-content-option');
                      if (drawerContentEl) {
                          drawerContentEl.style.display = 'none';
                      }
                      if (child.classList.contains('active')) {
                          clone.classList.add('active');
                      }
                      clone.style.display = 'flex';
                      drawerOptions.appendChild(clone);
                  });

                  const contentContainer = document.createElement('div');
                  contentContainer.classList.add('drawer-content-container');

                  childItems.forEach(child => {
                      const originalContent = child.querySelector('.drawer-content-option');
                      if (originalContent) {
                          const wrapper = document.createElement('div');
                          wrapper.classList.add('drawer-content-wrapper');
                          wrapper.setAttribute('data-content-for', child.getAttribute('data-option-id'));
                          wrapper.innerHTML = originalContent.innerHTML;
                          wrapper.style.display = 'none';
                          contentContainer.appendChild(wrapper);
                      }
                  });

                  drawerContentArea.appendChild(contentContainer);

                  drawerOptions.onclick = (event) => {
                      const clickedItem = event.target.closest('.option-item, .pdp-step-tile');
                      if (!clickedItem) return;

                      const itemId = clickedItem.getAttribute('data-option-id');


                      const allContent = drawer.querySelectorAll('.drawer-content-wrapper');
                      allContent.forEach(content => {
                          content.style.display = 'none';
                      });

                      const targetContent = drawer.querySelector(`.drawer-content-wrapper[data-content-for="${itemId}"]`);
                      if (targetContent) {
                          targetContent.style.display = 'block';
                      }

                      drawerOptions.querySelectorAll('.option-item, .pdp-step-tile').forEach(item => item.classList.remove('active'));
                      clickedItem.classList.add('active');

                      const mainItem = this.restructured.querySelector(`.option-item[data-option-id="${itemId}"], .pdp-step-tile[data-option-id="${itemId}"]`);
                      if (mainItem) {
                          const level = parseInt(mainItem.getAttribute('data-level'), 10);
                          // Drawer flow bypasses handleOptionClick — replicate
                          // its userAdvanced marker so applyCollapseState marks
                          // the column complete.
                          const ownColumn = mainItem.closest('.option-column, .pdp-step-column');
                          if (ownColumn) ownColumn.dataset.userAdvanced = 'true';
                          this.updateActiveState(mainItem, level);
                          this.handleRegularOption(level, itemId);
                          // Drive ATC + form input from the drawer leaf the same way
                          // a regular tile click does. Without this the dispatch
                          // never fires and the button stays in NO_SELECTION.
                          if (mainItem.dataset.isLeaf !== 'true') {
                              const notAvailable = this.isTerminalWithoutProduct(mainItem, itemId);
                              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false, notAvailable } }));
                              if (notAvailable) this.applyNotAvailableInventory();
                          }
                          this.updateProductInfo(mainItem);
                          this.applyCollapseState();
                      }

                      // Update preorder attribute based on final selected subvariant
                      this.updatePreorderAttribute();

                      document.dispatchEvent(new CustomEvent('variant:drawer:click', {detail: { optionElement: clickedItem }}))
                  };
              }

              this.openDrawer(drawer);

              const submitButton = drawer.querySelector('.drawer-submit-button');
              const newSubmitButton = submitButton.cloneNode(true);
              submitButton.parentNode.replaceChild(newSubmitButton, submitButton);

              newSubmitButton.addEventListener('click', () => {
                  this.closeDrawer(drawer);
              });
          });
      }

      buildDrawerBreadcrumb(optionId) {
          const titles = [];
          let node = this.restructured.querySelector(`.option-item[data-option-id="${optionId}"], .pdp-step-tile[data-option-id="${optionId}"]`);
          while (node) {
              const title = (node.dataset.optionTitle || node.textContent).trim();
              if (title) titles.unshift(title);
              const pid = node.dataset.parentId;
              node = pid
                  ? this.restructured.querySelector(`.option-item[data-option-id="${pid}"], .pdp-step-tile[data-option-id="${pid}"]`)
                  : null;
          }
          const nativeActive = this.restructured.querySelector('.pdp-step-tile--native.active');
          if (nativeActive?.dataset.optionTitle) titles.unshift(nativeActive.dataset.optionTitle.trim());
          return titles.join(' › ');
      }

      openDrawer(drawer) {
          const optionId = drawer.getAttribute('data-parent-option');
          const titleEl = drawer.querySelector('.header-drawer-option__title');
          if (optionId && titleEl) titleEl.textContent = this.buildDrawerBreadcrumb(optionId);
          drawer.classList.add('is-active');
          document.body.classList.add('drawer-open');
          // Show overlay
          const overlay = document.querySelector('.drawer-overlay');
          overlay.classList.add('is-active');
      }

      closeDrawer(drawer = null) {
          if (drawer) {
              // Always close the drawer without checking for variant selection
              drawer.classList.remove('is-active');
          } else {
              // If no specific drawer provided, close all drawers
              const allDrawers = document.querySelectorAll('.variant-options-drawer');
              allDrawers.forEach(d => {
                  d.classList.remove('is-active');
              });
          }

          // Only remove body class and hide overlay if no drawers are active
          const activeDrawers = document.querySelectorAll('.variant-options-drawer.is-active');
          if (activeDrawers.length === 0) {
              document.body.classList.remove('drawer-open');
              // Hide overlay
              const overlay = document.querySelector('.drawer-overlay');
              if (overlay) {
                  overlay.classList.remove('is-active');
              }
          }
      }
  }

  if (!window.customElements.get('variant-picker-steps-metafields')) {
      customElements.define('variant-picker-steps-metafields', VariantPickerStepsMetafields);
  }




  class BundleAddToCartButton extends HTMLElement {
      constructor() {
          super();
          this.button = this.querySelector('button');
          this.buttonText = this.button?.querySelector('.add-bundle-product__text');
          this.cartDrawer = document.querySelector('cart-drawer');
          this.quickAddModal = this.closest('quick-add-modal');
      }

      connectedCallback() {
          // For preorders, the main button must not add to cart directly.
          // popup.js intercepts the click and opens the popup; the popup button
          // then triggers add to cart via handlePopupButtonClick -> handleClick.
          // The preorder state can also be resolved later by a fitment leaf, so
          // the guard is evaluated per click instead of once at connect time.
          this.button?.addEventListener('click', () => {
              if (this.dataset.isPreorder === 'true') return;
              this.handleClick();
          });
          this.setupPopupButtonHandler();

          const candidatePicker = document.querySelector('variant-picker-steps-metafields');
          const hasChain = !!candidatePicker?.querySelector('.option-column .pdp-step-tile, .option-column .option-item, .pdp-step-tile--native');
          this.pickerEl = hasChain ? candidatePicker : null;
          if (this.pickerEl) {
              this._lastState = { resolved: false };
              this._handleVariantResolved = (e) => {
                  this._lastState = e.detail || { resolved: false };
                  this.applyState(this._lastState);
              };
              document.addEventListener('bundle:variant-resolved', this._handleVariantResolved);

              this._enforce = () => {
                  if (this._writing) return;
                  this.applyState(this._lastState);
              };
              this._buttonObserver = new MutationObserver(this._enforce);
              if (this.button) {
                  this._buttonObserver.observe(this.button, {
                      attributes: true,
                      attributeFilter: ['disabled'],
                      childList: true,
                      subtree: true,
                      characterData: true,
                  });
              }
              this.applyState({ resolved: false });
          }
      }

      isMobile() {
          return window.matchMedia('(max-width: 749px)').matches;
      }

      _computeExpected({ resolved = false, available = false, isPreorder = false, notAvailable = false } = {}) {
          const strings = this.i18n();
          if (notAvailable) return { label: strings.notAvailable, disabled: true };
          // On mobile the no-selection CTA stays enabled so it renders in the active
          // style instead of the faded disabled one; the tap is intercepted in
          // handleClick to open the picker sheet (never adds). Desktop stays disabled.
          if (!resolved) return { label: strings.noSelection, disabled: !this.isMobile() };
          if (isPreorder) return { label: strings.preorder, disabled: false };
          if (available) return { label: strings.add, disabled: false };
          return { label: strings.soldOut, disabled: true };
      }

      disconnectedCallback() {
          if (this._handleVariantResolved) {
              document.removeEventListener('bundle:variant-resolved', this._handleVariantResolved);
          }
          if (this._buttonObserver) {
              this._buttonObserver.disconnect();
          }
          if (this.popupElement && this.boundPopupHandler) {
              this.popupElement.removeEventListener('click', this.boundPopupHandler);
          }
          if (this.popupElement) {
              this.popupElement.removeEventListener('click', this.handlePopupButtonClick);
          }
          this._popupHandlerAttached = false;
      }

      i18n() {
          const formEl = document.querySelector('[id^="product-form-"]');
          const sectionId = this.closest('[data-section]')?.dataset?.section
              || (formEl?.getAttribute('id') || '').replace('product-form-', '')
              || '';
          const read = (id, fallback) => document.getElementById(`${id}-${sectionId}`)?.textContent?.trim() || fallback;
          return {
              noSelection: read('I18nAtcNoSelection', 'Choose your options'),
              add: read('I18nAtcAdd', 'Add to cart'),
              preorder: read('I18nAtcPreorder', 'Pre-Order Now'),
              soldOut: read('I18nAtcSoldOut', 'Sold Out'),
              notAvailable: read('I18nNotAvailable', 'Not available'),
          };
      }

      applyState({ resolved = false, available = false, isPreorder = false, notAvailable = false } = {}) {
          if (!this.button) return;
          this._writing = true;
          try {
              const { label, disabled } = this._computeExpected({ resolved, available, isPreorder, notAvailable });
              // Keep the popup gate (popup.js reads data-is-preorder) in sync with
              // the resolved state, not just the main product's metafield.
              this.dataset.isPreorder = isPreorder ? 'true' : 'false';
              const iconEl = this.button.querySelector('.add-bundle-product__icon');
              if (iconEl) iconEl.hidden = !resolved || (!available && !isPreorder);
              this.buttonText = this.button.querySelector('.add-bundle-product__text');
              if (this.buttonText && this.buttonText.textContent !== label) {
                  this.buttonText.textContent = label;
              }
              if (this.button.disabled !== disabled) this.button.disabled = disabled;
              this.button.classList.toggle('product-form__submit--no-selection', !resolved && !notAvailable);
              this.button.classList.toggle('product-form__submit--sold-out', notAvailable || (resolved && !available && !isPreorder));
          } finally {
              this._writing = false;
          }
      }
      
      setupPopupButtonHandler() {
          if (this._popupHandlerAttached) return;
          this.popupElement = document.querySelector('#preorderPopup');
          if (!this.popupElement) return;

          this.popupElement.addEventListener('click', this.handlePopupButtonClick);
          this._popupHandlerAttached = true;
      }

      handlePopupButtonClick = (e) => {
          const popupButton = e.target.closest('button[action-add-to-cart]');
          if (!popupButton) return;

          e.preventDefault();
          e.stopPropagation();

          if (popupButton.disabled) return;

          const bundleProductId = this.getBundleProductId();
          if (!bundleProductId) {
              console.error('Please make a selection before adding to cart');
              return;
          }

          popupButton.disabled = true;

          this.handleClick()
              .then(() => {
                  popupButton.disabled = false;
              })
              .catch((error) => {
                  console.error('Error adding bundle to cart:', error);
                  popupButton.disabled = false;
              });
      }

      async handleClick() {
          try {
              // No-selection CTA is enabled for styling but must not add to cart.
              // On mobile, open the picker sheet; on desktop, no-op.
              if (this.pickerEl && !(this._lastState && this._lastState.resolved)) {
                  if (this.isMobile()) {
                      document.querySelector('picker-mobile-sheet')?.openToFirstIncomplete?.();
                  }
                  return;
              }

              const bundleProductId = this.getBundleProductId();
              if (!bundleProductId) {
                  throw new Error('Please make a selection before adding to cart');
              }

              const quantity = this.getQuantity();

              // Set loading state at the start
              this.setLoadingState(true);

              await this.addToCart(bundleProductId, quantity);
              await this.updateCartDrawer();

              // Clear loading state on success
              this.setLoadingState(false);

              // Dispatch custom event to close popup after successful add to cart
              document.dispatchEvent(new CustomEvent('cart:item-added'));

          } catch (error) {
              this.handleError(error);
          } finally {
              // Ensure loading state is cleared even if there's an error
              this.setLoadingState(false);
          }
      }

      getBundleProductId() {
          const productContainer = this.closest('.product');
          if (!productContainer) return null;

          const variantIdInput = productContainer.querySelector('input[name="id"]');
          if (!variantIdInput) return null;

          const leaf = this.getLastSelectedOption();
          const bundleId = leaf?.getAttribute('data-bundle-product');
          return bundleId || variantIdInput.value;
      }

      getLastSelectedOption() {
          const container = this.quickAddModal || this.closest('.product') || document;
          const variantPicker = container.querySelector('variant-picker-steps-metafields');
          if (!variantPicker) return null;

          const activeTiles = Array.from(
              variantPicker.querySelectorAll('.pdp-step-tile.active, .option-item.active')
          );

          let deepest = null;
          let deepestLevel = -1;
          activeTiles.forEach(tile => {
              if (tile.classList.contains('pdp-step-tile--native')) return;
              if (!tile.hasAttribute('data-bundle-product')) return;
              const lvl = parseInt(tile.dataset.level || '0', 10);
              if (lvl > deepestLevel) {
                  deepestLevel = lvl;
                  deepest = tile;
              }
          });
          return deepest;
      }

      getActiveLineItemProperties() {
          const container = this.quickAddModal || this.closest('.product') || document;
          const variantPicker = container.querySelector('variant-picker-steps-metafields');
          if (!variantPicker) return [];

          const activeTiles = Array.from(
              variantPicker.querySelectorAll('.pdp-step-tile.active, .option-item.active')
          );

          const seenKeys = new Set();
          const pairs = [];
          activeTiles.forEach(tile => {
              if (tile.offsetParent === null) return;
              if (tile.classList.contains('pdp-step-tile--native')) return;
              const key = (tile.getAttribute('data-line-item-key') || '').trim();
              const value = (tile.getAttribute('data-line-item-value') || '').trim();
              if (!key || !value) return;
              if (seenKeys.has(key)) return;
              seenKeys.add(key);
              pairs.push([key, value]);
          });
          return pairs;
      }

      getVariantOptionLineItemProperties() {
          const container = this.quickAddModal || this.closest('.product') || document;
          const fieldsets = container.querySelectorAll(
              'variant-radios fieldset.product-form__controls, variant-selects fieldset.product-form__controls'
          );
          const pairs = [];
          fieldsets.forEach(fs => {
              const legend = fs.querySelector('legend.product-form__group-name');
              const checked = fs.querySelector('input[type="radio"]:checked');
              if (!legend || !checked) return;
              const textNode = [...legend.childNodes].find(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
              const rawKey = textNode ? textNode.textContent : legend.textContent;
              const key = rawKey.replace(/:\s*$/, '').trim();
              const value = (checked.value || '').trim();
              if (!key || !value) return;
              pairs.push([key, value]);
          });
          return pairs;
      }

      isColumnVisible(column) {
          return window.getComputedStyle(column).display !== 'none';
      }

      getQuantity() {
        // First try within product-form, then within the product container, fallback to 1
        let quantityInput = this.closest('product-form')?.querySelector('.quantity__input');
        
        if (!quantityInput) {
          const container = this.quickAddModal || this.closest('.product');
          quantityInput = container?.querySelector('.quantity__input');
        }
        
        return quantityInput ? parseInt(quantityInput.value, 10) : 1;
      }

      async addToCart(bundleProductId, quantity) {
          // Prepare properties object
          let properties = {};
          if (this.getLastSelectedOption() && this.getLastSelectedOption().getAttribute('data-model-name')) {
            const lastSelectedOption = this.getLastSelectedOption();
            const modelNameKeyValue = lastSelectedOption?.getAttribute('data-model-name');
            const key = modelNameKeyValue.split(':')[0];
            const value = modelNameKeyValue.split(':')[1];
            properties[key] = value;
          }

          //   for (const [vKey, vValue] of this.getVariantOptionLineItemProperties()) {
          //     properties[vKey] = vValue;
          //   }

          for (const [lipKey, lipValue] of this.getActiveLineItemProperties()) {
            properties[lipKey] = lipValue;
          }

          // Check for pre-order text property (scope to current product)
          const container = this.quickAddModal || this.closest('.product') || document;
          const preorderInput = container.querySelector('input[name="properties[Pre-order]"]');
          if (preorderInput && preorderInput.value.trim() !== '') {
            properties['Pre-order'] = preorderInput.value;
          }

          // Remove the setLoadingState from here since it's handled in handleClick
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  items: [{
                      id: parseInt(bundleProductId, 10),
                      quantity: quantity,
                      properties: properties
                  }]
              })
          });

          const data = await response.json();

          if (!response.ok) {
              this.showErrorMessage(data.message);
              throw new Error(data.description || 'Error adding item to cart');
          }

          return data;
      }

      async updateCartDrawer() {
          if (!this.cartDrawer) return;

          const response = await fetch(
              `${window.Shopify.routes.root}cart?sections=${(window.cartDrawerSectionId ? window.cartDrawerSectionId() : 'cart-drawer')},cart-icon-bubble`
          );
          const sectionsData = await response.json();

          this.cartDrawer.renderContents({ sections: sectionsData });
      }

      handleError(error) {
          console.error('Add to cart error:', error);
          this.showErrorMessage(error.message);
          // Remove setLoadingState from here since it's handled in the finally block
      }

      showErrorMessage(message) {
          // Create banner container
          const banner = document.createElement('div');
          banner.classList.add('banner', 'banner--error');

          // Create banner content
          const bannerContent = document.createElement('div');
          bannerContent.classList.add('banner__content');

          // Add message
          const messageText = document.createElement('p');
          messageText.classList.add('banner__message');
          messageText.textContent = message;

          // Add close button
          const closeButton = document.createElement('button');
          closeButton.classList.add('banner__close');
          closeButton.setAttribute('aria-label', 'Close');
          closeButton.innerHTML = '×';
          closeButton.onclick = () => banner.remove();

          // Assemble banner
          bannerContent.appendChild(messageText);
          bannerContent.appendChild(closeButton);
          banner.appendChild(bannerContent);

          // Add to document
          document.querySelector('.bundle-pdp-section')?.appendChild(banner);

          // Auto-remove after 5 seconds
          setTimeout(() => {
              if (banner.parentNode) {
                  banner.remove();
              }
          }, 5000);
      }

      setLoadingState(isLoading) {
          this.button.disabled = isLoading;
          this.button.classList.toggle('loading', isLoading);
          this.button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);
      }
  }

  if (!customElements.get('bundle-add-to-cart-button')) {
      customElements.define('bundle-add-to-cart-button', BundleAddToCartButton);
  }
